/* eslint-disable no-console -- We want to log errors */
import * as ToggleMenuFlyout from '@ori-events/toggle-menu-flyout';
import { NewUser, NewUserFilled } from '@ori-ui/icons';
import { IconButton, styled } from '@ori-ui/mui';
import { axios } from '@ori/axios';
import useTestId from '@ori/testid-generator';
import type { VFC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { VipOrder } from './VipOrder';
import { DESKTOP_BREAKPOINT } from '~/constants';
import { useAppConfigurationContext } from '~/contexts/AppConfigurationContext';
import { useNavigationContext } from '~/contexts/NavigationContext';
import { dispatchToggleMenu, getCustomAxios, onEnterKey, vipAccessUrl } from '~/helpers';
import type { VipAccessResponse } from '~/models';
import { log } from '~/utils';

const VipOrderIconRoot = styled(IconButton)(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.grey.A700,
  cursor: 'pointer',
  display: 'flex',
  fontSize: theme.typography.h4.fontSize,
  height: 'fit-content',
  marginRight: 'auto',

  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(6),
  },

  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    marginLeft: 'auto',
  },

  [theme.breakpoints.up('lg')]: {
    marginLeft: theme.spacing(4),
  },

  [theme.breakpoints.up('xl')]: {
    marginLeft: theme.spacing(6),
  },
}));

export const VipOrderIcon: VFC = () => {
  const { activeMenu } = useNavigationContext();
  const [consultantData, setConsultantData] = useState<VipAccessResponse>({});
  const isPopupOpen = activeMenu === ToggleMenuFlyout.MenuTypes.VipOrder;
  const ref = useRef(null);
  const { getTestId } = useTestId();
  const { currentLanguage } = useAppConfigurationContext();

  useEffect(() => {
    const cancelSource = axios.CancelToken.source();

    const getData = async () => {
      try {
        const { data } = await getCustomAxios(currentLanguage).get<VipAccessResponse>(
          vipAccessUrl,
          { cancelToken: cancelSource.token },
        );
        setConsultantData(data);
      } catch (error) {
        if (axios.isCancel(error)) {
          log.info('consultantData data request canceled in useEffect cleanup');
        } else {
          console.warn(error);
        }
      }
    };

    getData();

    return () => {
      cancelSource.cancel();
    };
  }, [currentLanguage]);

  const onClick = useCallback(() => {
    dispatchToggleMenu(ToggleMenuFlyout.MenuTypes.VipOrder, activeMenu);
  }, [activeMenu]);

  if (!(consultantData.VipAccessData && consultantData.VipAccessLabels)) {
    return null;
  }

  return (
    <div ref={ref}>
      <VipOrderIconRoot
        data-testid={getTestId('vip-icon-root')}
        onClick={onClick}
        onKeyDown={onEnterKey(onClick)}
      >
        {isPopupOpen ? <NewUserFilled fontSize="inherit" /> : <NewUser fontSize="inherit" />}
      </VipOrderIconRoot>
      {isPopupOpen ? (
        <VipOrder
          vipAccessData={consultantData.VipAccessData}
          vipAccessLabels={consultantData.VipAccessLabels}
        />
      ) : null}
    </div>
  );
};
